import { Subject } from 'rxjs';

import { Tag, TagGroup, TaskState } from '../../shared/models/misc.model';
import { TagRestriction } from './model';
import { TaskProcessContext } from './task-process-context.class';

/**
 * This class holds the tag groups to be passed around the task process page and components.
 * It also holds some code that handles the tag dropdowns displayed on the task process page and the message audit dialog.
 */
export class TagsContext {
  taskId: number;
  allTagGroups: TagGroup[];
  routingTagGroup: TagGroup;
  hasMandatoryAndVisibleTagsMissing: boolean; // Used for enabling/disabling the send button
  tagsChange$ = new Subject<void>();

  taskProcessContext: TaskProcessContext;
  taskState: TaskState;

  constructor(
    tagGroups: TagGroup[],
    routingTagGroup: TagGroup,
    taskId: number,
    taskState: TaskState,
    taskProcessContext
  ) {
    this.taskState = taskState;
    this.taskProcessContext = taskProcessContext; // only passed if we are displaying the tags of the processed tag (null otherwise)
    this.taskId = taskId;
    this.allTagGroups = [...tagGroups];
    if (routingTagGroup) {
      routingTagGroup.isRouting = true;
      this.allTagGroups.unshift(routingTagGroup); // Displayed first in the list
      this.routingTagGroup = routingTagGroup;
    }
    this.updateMandatoryAndMissingTags();
  }

  destroy() {
    this.tagsChange$.complete(); // unsubscribe our subscribers, so that they don't need to pipe(takeUntil(this.ngOnDestroy))
  }

  // Called when the processed task has been reloaded, following a websocket notification
  updateTagsValue(routingTagId: number, tagIds: number[]) {
    const newTagIds = [routingTagId, ...tagIds];
    for (const tagGroup of this.allTagGroups) {
      for (const tag of tagGroup.tags) {
        if (newTagIds.includes(tag.id) && !tag.isCategory) {
          tagGroup.selectedTagId = tag.id;
        }
      }
    }
    this.tagsChanged();
  }

  // used by <app-select-tag> component
  isTagVisible(tag: Tag): boolean {
    return this.isTagOrTagGroupVisible(tag);
  }

  // used in event handler of <app-select-tag> and in updateTagsValue() above
  tagsChanged() {
    this.updateMandatoryAndMissingTags();

    // Update the favorites in answer editor after a tag change,
    // on the tpp if a tag whas changed in the action log modal,
    // and if a tag group was set to 'None' because its current value was dependent on a another tag value that just changed
    this.tagsChange$.next();
  }

  getRoutingTagGroup(): TagGroup {
    return this.routingTagGroup;
  }

  getLangageCode() {
    const group = this.allTagGroups.find(tg => tg.isLanguage);
    return group ? group.tags.find(t => t.id === group.selectedTagId).languageCode : null;
  }

  /**
   * returns the list of tag groups whose current value depends upon the given tag value
   */
  getTagGroupsWhoseValueDependUponTagValue(dependeeTagValue: Tag): TagGroup[] {
    const result = [];
    for (const tagGroup of this.allTagGroups) {
      // Don't return the group that we are actually tagging.
      // It should not happen for regular tags except for routing tags
      // where depends_upon does mean the same thing :(
      // (it actually refers to teams that have the right to route to this team tag)
      // So, for routing tags we do have tags depending on tags of the same group and
      // we don't want to reset that group.
      const tagGroupTagIds = tagGroup.tags.map(t => t.id);
      if (tagGroupTagIds.includes(dependeeTagValue.id)) {
        continue;
      }

      const tagValue = tagGroup.tags.find(t => t.id === tagGroup.selectedTagId);
      const dependeeIds = tagValue.dependsUpon ? tagValue.dependsUpon.map(d => d.tag_id) : [];
      if (dependeeIds.includes(dependeeTagValue.id)) {
        result.push(tagGroup);
      }
    }
    return result;
  }

  /**
   * Tells whether the current tag values match the given restrictions
   * (used to hide/show the "send and trigger survey" button according to the tags' current values)
   * Retuns true iff the survey can be sent
   */
  matchesRestrictions(tagRestrictions: TagRestriction[]): boolean {
    let matchesAll = true;
    for (const restriction of tagRestrictions) {
      const tagGroup = this.allTagGroups.find(tg => tg.id === restriction.tagGroupId);
      if (tagGroup) {
        if (!restriction.tagIds.includes(tagGroup.selectedTagId)) {
          matchesAll = false;
        }
      } else {
        matchesAll = false;
      }
    }
    return matchesAll;
  }

  /**
   * Determines if there is at least one mandatory and visible tagGroup missing from the task, and updates those that match
   * Used for enabling/ disabling the send button on the task process page
   *
   * Called once after getting the task and then each time after a tag value is changed through the dropdown
   */
  private updateMandatoryAndMissingTags() {
    this.hasMandatoryAndVisibleTagsMissing = false;
    this.allTagGroups.forEach(tagGroup => {
      tagGroup.isVisible = this.isTagOrTagGroupVisible(tagGroup);
      tagGroup.isVisibleAndNotRouting = !tagGroup.isRouting && tagGroup.isVisible;

      if (tagGroup.noneTagId === tagGroup.selectedTagId && tagGroup.isMandatory === 1 && tagGroup.isVisible) {
        this.hasMandatoryAndVisibleTagsMissing = true;
        tagGroup.isMandatoryAndMissing = true;
      } else {
        tagGroup.isMandatoryAndMissing = false;
      }
    });
  }

  /**
   * Determines if the tag or tagGroup is visible in the UI
   */
  private isTagOrTagGroupVisible(tg: Tag | TagGroup) {
    const selectedTagIds = this.getAllSelectedTagIds();
    return (
      tg.dependsUpon === null ||
      tg.dependsUpon.length === 0 ||
      tg.dependsUpon.filter(d => selectedTagIds.find(id => id === d.tag_id)).length > 0
    );
  }

  private getAllSelectedTagIds() {
    return this.allTagGroups.map(tag => tag.selectedTagId);
  }
}
